import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Waypoints, Loader2, ShieldAlert } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function SsoPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [error, setError] = useState(null);
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;
    const code = params.get("code");
    if (!code) {
      setError("Missing login code. Please use the link from your main dashboard.");
      return;
    }
    // Remove the one-time code from the address bar/history immediately.
    window.history.replaceState({}, document.title, "/sso");

    (async () => {
      try {
        const { data } = await api.post("/auth/sso/exchange", { code });
        await login(data.token, data.user);
        navigate("/app", { replace: true });
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || "This login link is invalid or expired.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#F5F6F8] mesh-grid flex items-center justify-center px-4" data-testid="sso-page">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass rounded-2xl p-8 w-full max-w-sm text-center"
      >
        <div className="mx-auto w-fit">
          <img src="/logo-jbn-icon.png" alt="JobBasket Network" className="w-12 h-12 object-contain" />
        </div>
        {error ? (
          <>
            <ShieldAlert className="w-8 h-8 text-amber-400 mx-auto mt-5" />
            <h1 className="text-xl font-bold font-display mt-3" data-testid="sso-error">Couldn't sign you in</h1>
            <p className="text-sm text-slate-500 mt-2">{error}</p>
            <Link
              to="/auth"
              data-testid="sso-back-to-login"
              className="inline-block mt-6 text-sm px-5 py-2.5 rounded-lg bg-amber-500 text-black font-semibold hover:bg-amber-400 transition-colors"
            >
              Sign in with email instead
            </Link>
          </>
        ) : (
          <>
            <Loader2 className="w-8 h-8 text-amber-500 mx-auto mt-5 animate-spin" />
            <h1 className="text-xl font-bold font-display mt-3" data-testid="sso-signing-in">Signing you in…</h1>
            <p className="text-sm text-slate-500 mt-2">Securely connecting your account.</p>
          </>
        )}
      </motion.div>
    </div>
  );
}
